/**
 * Debate sheet — the per-match chat surface (brief §6.5 / D12).
 *
 * Conversation is scoped to a single match — no global Ask Falcon
 * surface exists. Bottom sheet that slides up from below the Match
 * detail. POSTs each user message to /api/v1/match/:id/debate and
 * appends the persona reply to the thread.
 *
 * State is in-memory (resets when the sheet closes). Phase B.1 could
 * persist threads per (user, match) in Postgres if there's demand.
 */

import { useEffect, useRef, useState } from 'react';
import type { DebateMessage, EdgeCitation } from '@sharp8/types';
import { haptic, tg } from '../lib/telegram';

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1';

interface Props {
  matchId: string;
  matchLabel: string;
  open: boolean;
  onClose(): void;
}

const PERSONA_AVATARS: Record<string, { bg: string; fg: string; letter: string; name: string }> = {
  'sharp8-house': { bg: 'linear-gradient(135deg,#4ED5DB,#2EA8AE)', fg: '#0A1322', letter: 'S', name: 'Sharp8 House' },
  'tanaka':       { bg: 'linear-gradient(135deg,#1A3A6B,#0E1F3D)', fg: '#fff',    letter: 'T', name: 'Tanaka' },
  'sofia':        { bg: 'linear-gradient(135deg,#FF6B9D,#C92A6F)', fg: '#fff',    letter: 'S', name: 'Sofia' },
  'arjun':        { bg: 'linear-gradient(135deg,#2D6B47,#1A4A30)', fg: '#fff',    letter: 'A', name: 'Arjun' },
  'caleb':        { bg: 'linear-gradient(135deg,#D4831F,#8B5A0C)', fg: '#fff',    letter: 'C', name: 'Caleb' },
  'lucas':        { bg: 'linear-gradient(135deg,#6B47B8,#422D7A)', fg: '#fff',    letter: 'L', name: 'Lucas' },
};

const PROMPT_SUGGESTIONS = [
  "Why this line and not the alternative?",
  "What if Eze starts?",
  "How does the second-opinion model differ?",
  "What's the BTTS read here?",
];

export function DebateSheet({ matchId, matchLabel, open, onClose }: Props) {
  const [messages, setMessages] = useState<DebateMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Reset when the sheet closes (or matchId changes).
  useEffect(() => {
    if (!open) {
      setMessages([]);
      setInput('');
      setError(null);
    }
  }, [open, matchId]);

  // Lock body scroll while sheet is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  async function send(rawQuery: string) {
    const query = rawQuery.trim();
    if (!query || sending) return;

    // Build conversation history BEFORE appending the new user turn —
    // the LLM gets the prior turns + the current query as a separate
    // last user message. Cap matches server-side sanitizeHistory().
    // Local 'system' refusal bubbles map to 'assistant' so the model
    // sees its own past refusal in the thread.
    const history = messages
      .map((m) => ({
        role: m.role === 'user' ? ('user' as const) : ('assistant' as const),
        content: m.content,
      }))
      .slice(-12);

    // Append the user message immediately
    const userMsg: DebateMessage = {
      id: `local-${Date.now()}`,
      matchId,
      role: 'user',
      personaKey: null,
      content: query,
      citations: [],
      createdAt: new Date().toISOString(),
    };
    setMessages((m) => [...m, userMsg]);
    setInput('');
    setSending(true);
    setError(null);
    haptic('light');

    try {
      const initData = tg()?.initData ?? '';
      const res = await fetch(`${API_BASE.replace(/\/$/, '')}/match/${matchId}/debate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(initData ? { 'X-Telegram-Init-Data': initData } : {}),
        },
        body: JSON.stringify({ query, lang: 'en', history }),
      });
      if (!res.ok) throw new Error(`debate failed: ${res.status}`);
      const data = await res.json() as { replies: DebateMessage[]; refused: boolean; refusalReason: string | null };

      if (data.refused) {
        setMessages((m) => [...m, {
          id: `local-refusal-${Date.now()}`,
          matchId,
          role: 'system',
          personaKey: null,
          content: data.refusalReason ?? 'The engine refuses to predict — no edge available.',
          citations: [],
          createdAt: new Date().toISOString(),
        }]);
      } else {
        setMessages((m) => [...m, ...data.replies]);
      }
      haptic('medium');
    } catch (e: any) {
      setError(String(e?.message ?? e));
      haptic('light');
    } finally {
      setSending(false);
    }
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    send(input);
  }

  return (
    <>
      {/* Backdrop */}
      <div className="debate-backdrop" onClick={onClose} />

      {/* Sheet */}
      <aside className="debate-sheet" role="dialog" aria-label="Debate">
        <header className="debate-sheet__header">
          <div className="debate-sheet__handle" />
          <div className="debate-sheet__title">
            <div className="debate-sheet__eyebrow">The Debate</div>
            <div className="debate-sheet__match">{matchLabel}</div>
          </div>
          <button className="debate-sheet__close" aria-label="Close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>

        <div className="debate-sheet__body" ref={scrollRef}>
          {messages.length === 0 ? (
            <div className="debate-sheet__empty">
              <p>
                Ask anything about this match. The engine answers from the same
                synthesis it used to issue the edge call — no extra speculation.
              </p>
              <div className="debate-sheet__prompts">
                {PROMPT_SUGGESTIONS.map((p) => (
                  <button key={p} className="debate-sheet__prompt" onClick={() => send(p)}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <ul className="debate-sheet__msgs">
              {messages.map((m) => (
                <Message key={m.id} m={m} />
              ))}
              {sending && <TypingIndicator />}
            </ul>
          )}
          {error && (
            <div className="debate-sheet__error">Couldn't reach the engine: {error}</div>
          )}
        </div>

        <form className="debate-sheet__input" onSubmit={onSubmit}>
          <input
            type="text"
            placeholder="Ask the panel…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={sending}
            autoFocus
          />
          <button type="submit" disabled={sending || !input.trim()} aria-label="Send">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 12L21 3l-9 18-2-7-7-2z" />
            </svg>
          </button>
        </form>
      </aside>
    </>
  );
}

function Message({ m }: { m: DebateMessage }) {
  if (m.role === 'user') {
    return (
      <li className="debate-msg debate-msg--user">
        <div className="debate-msg__bubble">{m.content}</div>
      </li>
    );
  }
  if (m.role === 'system') {
    return (
      <li className="debate-msg debate-msg--system">
        <div className="debate-msg__bubble">{m.content}</div>
      </li>
    );
  }
  // Persona reply
  const av = m.personaKey ? PERSONA_AVATARS[m.personaKey] : null;
  return (
    <li className="debate-msg debate-msg--persona">
      {av && (
        <span className="debate-msg__avatar" style={{ background: av.bg, color: av.fg }}>
          {av.letter}
        </span>
      )}
      <div className="debate-msg__col">
        {av && <div className="debate-msg__name">{av.name}</div>}
        <div className="debate-msg__bubble">{m.content}</div>
        {m.citations.length > 0 && (
          <div className="debate-msg__cites">
            {m.citations.map((c, i) => <Citation key={i} c={c} />)}
          </div>
        )}
      </div>
    </li>
  );
}

function Citation({ c }: { c: EdgeCitation }) {
  return <span className="debate-msg__cite">{c.source}</span>;
}

function TypingIndicator() {
  return (
    <li className="debate-msg debate-msg--persona">
      <span className="debate-msg__avatar" style={{ background: 'var(--ink-dim)', color: '#fff' }}>…</span>
      <div className="debate-msg__col">
        <div className="debate-msg__bubble debate-msg__bubble--typing">
          <span /><span /><span />
        </div>
      </div>
    </li>
  );
}
